import PropTypes from "prop-types";
import React from "react";
import { useTranslation } from "react-i18next";
import {
  Bar,
  BarChart,
  CartesianGrid,
  LabelList,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import { getNameByLocation, isEmpty } from "../../../../utils/utils";

const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload?.length) {
    return (
      <div className="custom-tooltip" dir="ltr">
        <p className="label">{`${label}: ${payload[0].value}%`}</p>
      </div>
    );
  }

  return null;
};

CustomTooltip.propTypes = {
  active: PropTypes.bool,
  payload: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    })
  ),
  label: PropTypes.string,
};

export default function ColumnChart({ items }) {
  const { i18n } = useTranslation();
  const isLtr = i18n.language === "en";
  const index = items[0];
  // if there is no benchmark, the second item in the array is null
  // const benchmark = items[1]?.name || null;
  const benchmark = getNameByLocation(items[1], i18n) || null;

  if (isEmpty(index?.statistics?.index_return[0])) {
    return null;
  }
  const data = [
    {
      name: index?.name,
      Returns: (index?.statistics?.index_return[0]["15y"] * 100).toFixed(2),
      fill: "#33469A",
    },
  ];
  if (benchmark) {
    data.push({
      name: benchmark,
      Returns: (index?.statistics?.benchmark_return[0]["15y"] * 100).toFixed(2),
      fill: "#FF0000",
    });
  }

  const maxValue = Math.max(...data.map((item) => parseFloat(item.Returns)));
  const minValue = Math.min(...data.map((item) => parseFloat(item.Returns)));
  const yAxisDomain = [
    minValue < 0 ? Math.floor(minValue) : 0,
    Math.ceil(maxValue),
  ];

  const generateTicks = (min, max) => {
    const interval = 2;
    const ticksArray = new Set();
  
    for (let i = min; i <= max; i += interval) {
      ticksArray.add(i.toFixed());
    }
  
    if (!ticksArray.has("0")) {
      ticksArray.add("0");
    }
  
    return [...ticksArray].sort((a, b) => a - b);
  };
  

  const ticks = generateTicks(yAxisDomain[0], yAxisDomain[1]);

  const renderCustomBarLabel = ({ x, y, width, height, value }) => {
    return (
      <text
        x={x + width / 2}
        y={y}
        fill="#000"
        textAnchor="middle"
        dy={-6}
        direction="ltr"
      >
        {`${value}%`}
      </text>
    );
  };

  const renderCustomAxisTick = (props) => {
    const { x, y, payload } = props;

    return (
      <g transform={`translate(${x},${y})`}>
        <text x={0} y={6} dy={0} textAnchor={isLtr ? "end" : "start"}>
          {payload.value}%
        </text>
      </g>
    );
  };

  return (
    <div style={{ width: "100%", display: "flex", justifyContent: "center" }}>
      <ResponsiveContainer width="100%" height={300}>
        <BarChart
          data={data}
          margin={{
            top: 20,
            right: 30,
            left: 20,
            bottom: 5,
          }}
        >
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="name" style={{ direction: "ltr" }} />
          <YAxis
            domain={yAxisDomain}
            ticks={ticks}
            tick={renderCustomAxisTick}
          />
          <Tooltip content={<CustomTooltip />} />

          <Bar dataKey="Returns" minPointSize={20} barSize={100}>
            <LabelList
              dataKey="Returns"
              position="top"
              style={{ fill: "#000" }}
              content={renderCustomBarLabel}
            />
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}
